import { environment } from '../../../environments/environment';

declare const mapboxgl: typeof import('mapbox-gl');
declare const MapboxGeocoder: typeof import('@mapbox/mapbox-gl-geocoder');

/**
 * On-demand loader for Mapbox GL JS and the geocoder plugin.
 *
 * These used to be two `defer` <script> tags in `src/index.html`. That cost
 * 245 KB over the wire on *every* page view, map or not - but the bigger
 * problem was ordering: classic `defer` scripts and `type=module` scripts
 * share one in-order deferred execution list, ordered by document position.
 * mapbox-gl.js sat in <head> and main.js at the end of <body>, so ~800 KB of
 * Mapbox had to compile on the main thread before Angular could bootstrap -
 * on `/sales-forecast/*`, where the paid traffic lands and no map exists at
 * all. See `todos/optimization.md`, item 4.
 *
 * Plain module functions rather than an Angular service on purpose: this lets
 * `app-routing.module.ts` fire `preloadMapbox()` from inside `loadComponent`,
 * so the CDN fetch starts at the same instant as the route chunk instead of
 * waiting behind it.
 *
 * Still loaded from api.mapbox.com rather than bundled from npm: there is no
 * CDN in front of our origin yet (optimization.md item 3), so a self-hosted
 * copy would come from one EC2 box in Virginia instead of a Mapbox edge with
 * brotli. Worth revisiting once item 3 lands.
 */

const GL_VERSION = 'v2.15.0';
const GEOCODER_VERSION = 'v5.0.0';

const GL_JS = `https://api.mapbox.com/mapbox-gl-js/${GL_VERSION}/mapbox-gl.js`;
const GL_CSS = `https://api.mapbox.com/mapbox-gl-js/${GL_VERSION}/mapbox-gl.css`;
const GEOCODER_JS = `https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/${GEOCODER_VERSION}/mapbox-gl-geocoder.min.js`;
const GEOCODER_CSS = `https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/${GEOCODER_VERSION}/mapbox-gl-geocoder.css`;

/**
 * Per attempt, not for the whole load. Long enough not to abandon a slow but
 * working connection, short enough that three attempts still resolve inside
 * about half a minute. A timeout does not cancel the `<script>` it gave up on,
 * so `recoverLateArrival()` checks whether it landed anyway before the next
 * attempt spends anything.
 */
const ATTEMPT_TIMEOUT_MS = 10_000;

/** Delay before attempts 2 and 3. Its length is also the attempt count. */
const RETRY_BACKOFF_MS = [500, 2_000];

/**
 * Thrown when a bundle loaded but did not define what it should - a corrupted
 * CDN response, or a captive portal answering 200 with its own HTML. Retrying
 * *immediately* cannot fix that, so it skips the backoff and fails at once.
 *
 * It does still drop the asset from the cache on the way out (see
 * `forgetAsset`): the request itself succeeded, so without that the resolved
 * entry would be reused forever and every later attempt - the retry button,
 * the `online` handler - would short-circuit to the same error without ever
 * asking the network again.
 */
class MapboxContractError extends Error {}

let glPromise: Promise<typeof mapboxgl> | null = null;
let geocoderPromise: Promise<typeof MapboxGeocoder> | null = null;

/** True while a cached rejection is the only thing callers would get back. */
let hasFailed = false;

type RetryListener = () => void;
const retryListeners = new Set<RetryListener>();

let onlineRecoveryInstalled = false;

/**
 * One promise per URL, so a bundle already on the page is never re-requested
 * and concurrent callers share one request.
 *
 * A *failed* entry is deleted rather than kept. That matters more than it
 * looks: an earlier version of this file looked for an existing `<script>` tag
 * and attached a `load` listener to it, which never fires if the tag had
 * already finished - so any retry would have hung until its own timeout.
 */
const assetPromises = new Map<string, Promise<void>>();

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Drops a cached asset so the next attempt issues a real request for it. */
function forgetAsset(url: string): void {
  assetPromises.delete(url);
}

function loadScript(src: string): Promise<void> {
  const cached = assetPromises.get(src);
  if (cached) return cached;

  const work = new Promise<void>((resolve, reject) => {
    // Any tag here is left over from a failed attempt - the resolved-promise
    // path above is the only way a working one gets reused.
    document
      .querySelectorAll(`script[src="${src}"]`)
      .forEach((el) => el.remove());

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });

  assetPromises.set(src, work);
  // Delete only our *own* entry. A timed-out attempt has already had its key
  // dropped by `forgetAsset`, and attempt 2 may have stored its own live
  // promise under it by the time attempt 1's abandoned <script> finally
  // errors. An unguarded delete here would evict that live entry, sending the
  // next caller back into `loadScript` - which removes the in-flight <script>
  // from the DOM and starts a third download of the same 800 KB.
  work.catch(() => {
    if (assetPromises.get(src) === work) assetPromises.delete(src);
  });
  return work;
}

/**
 * Awaited rather than fire-and-forget: mapbox-gl.css positions the canvas and
 * the controls, so building a map before it lands gives a frame of overflowing
 * canvas and unstyled controls.
 *
 * A failure here resolves anyway - a missing stylesheet is cosmetic, and
 * failing the whole load over it would cost the visitor the map as well. The
 * cache entry is still dropped, so the next attempt re-tries it.
 */
function loadStylesheet(href: string): Promise<void> {
  const cached = assetPromises.get(href);
  if (cached) return cached;

  const work: Promise<void> = new Promise((resolve) => {
    document
      .querySelectorAll(`link[href="${href}"]`)
      .forEach((el) => el.remove());

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.crossOrigin = 'anonymous';
    link.onload = () => resolve();
    link.onerror = () => {
      console.error(`[mapbox] failed to load stylesheet ${href}`);
      // Guarded for the same reason as in `loadScript`: by now a later attempt
      // may own this key, and evicting its entry would re-request the asset.
      if (assetPromises.get(href) === work) assetPromises.delete(href);
      resolve();
    };
    document.head.appendChild(link);
  });

  assetPromises.set(href, work);
  return work;
}

/**
 * `assets` are dropped from the cache when the timeout fires, and that is the
 * whole point of passing them.
 *
 * A request that *errors* deletes its own entry (see `loadScript`), but one
 * that simply never settles - a dropped fetch on a phone, which is the failure
 * this retry exists for - leaves a pending entry behind. Without this the next
 * attempt would call `loadScript`, get that same dead promise back, and burn
 * another full timeout without ever reaching the network: three attempts, one
 * request, ~30 s of nothing.
 *
 * Re-requesting is safe even when only one of the pair hung, because
 * `recoverLateArrival()` returns the global before any retry is spent.
 */
function withTimeout<T>(
  work: Promise<T>,
  what: string,
  assets: string[]
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      assets.forEach(forgetAsset);
      reject(
        new Error(
          `[mapbox] ${what} did not load within ${ATTEMPT_TIMEOUT_MS}ms`
        )
      );
    }, ATTEMPT_TIMEOUT_MS);

    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/**
 * A timed-out attempt abandons its `<script>` but cannot cancel it, so the
 * bundle may have arrived a moment later and be sitting on `window` perfectly
 * usable. Without this check the visitor keeps an error state over a working
 * library.
 */
function recoverLateArrival<T>(globalName: string): T | null {
  const value = (window as any)[globalName] as T | undefined;
  if (value) {
    console.warn(
      `[mapbox] ${globalName} arrived after its attempt had given up; using it`
    );
    // Whatever this attempt thought about the asset is now wrong.
    forgetAsset(globalName === 'mapboxgl' ? GL_JS : GEOCODER_JS);
    return value;
  }
  return null;
}

/**
 * Runs `attempt` up to `RETRY_BACKOFF_MS.length + 1` times.
 *
 * A dropped request for a 220 KB asset on a phone is the common failure here
 * and a retry fixes it, which is why this exists at all. It is a different
 * trade from the gtag retry that `optimization.md` removed: a lost analytics
 * event costs nothing, whereas `/` with no Mapbox is not a page.
 */
async function withRetry<T>(
  attempt: () => Promise<T>,
  what: string,
  globalName: string
): Promise<T> {
  let lastError: unknown;

  for (let i = 0; i <= RETRY_BACKOFF_MS.length; i++) {
    try {
      return await attempt();
    } catch (error) {
      lastError = error;

      const late = recoverLateArrival<T>(globalName);
      if (late) return late;

      // The bytes arrived and were not what they should be. Asking again for
      // the same URL will produce the same answer.
      if (error instanceof MapboxContractError) break;

      const backoff = RETRY_BACKOFF_MS[i];
      if (backoff === undefined) break;

      console.warn(
        `[mapbox] ${what} attempt ${i + 1} failed, retrying in ${backoff}ms`,
        error
      );
      await delay(backoff);
    }
  }

  throw lastError;
}

/**
 * Reinstates the network-recovery listener. Installed on first use rather than
 * at module load so a page that never touches a map never adds a listener.
 */
function installOnlineRecovery(): void {
  if (onlineRecoveryInstalled || typeof window === 'undefined') return;
  onlineRecoveryInstalled = true;

  window.addEventListener('online', () => {
    if (!hasFailed) return;

    console.warn(
      '[mapbox] connection is back after a failed load; inviting another attempt'
    );
    resetMapboxLoadState();

    for (const listener of retryListeners) {
      try {
        listener();
      } catch (error) {
        console.error('[mapbox] a retry listener threw', error);
      }
    }
  });
}

/**
 * Clears cached rejections so the next `loadMapboxGl()`/`loadMapboxGeocoder()`
 * really tries again.
 *
 * Rejections used to be cached on the reasoning that a blocked CDN stays
 * blocked. That is true of an ad blocker and false of everything else, and
 * api.mapbox.com is a library CDN rather than a tracker host - so the blocked
 * case is the rare one and a transient failure was being made permanent.
 *
 * Successful loads are untouched: their entries in `assetPromises` are already
 * resolved, so a reset costs one microtask rather than a second download.
 *
 * A no-op when nothing has failed, which is what keeps concurrent callers from
 * tripping over each other. `CustomRouteReuseStrategy` retains the map route,
 * so a detached `MapboxComponent` survives a navigation with its
 * `onMapboxRetryAvailable` listener still registered - one `online` event then
 * reaches two live subscribers, and each calls this. Without the guard the
 * second reset would null out the first one's in-flight promise, orphaning it:
 * a third caller would start yet another load, and the orphan's `.catch` would
 * later set `hasFailed` again even if the live attempt had succeeded.
 */
export function resetMapboxLoadState(): void {
  if (!hasFailed) return;
  glPromise = null;
  geocoderPromise = null;
  hasFailed = false;
}

/**
 * Registers a callback for "it is worth trying again now" - currently the
 * `online` event after a failure. Returns its own unsubscribe.
 */
export function onMapboxRetryAvailable(listener: RetryListener): () => void {
  retryListeners.add(listener);
  return () => {
    retryListeners.delete(listener);
  };
}

/**
 * Resolves with the global `mapboxgl`, with `accessToken` already set.
 *
 * Idempotent and single-flight: concurrent callers share one promise. On
 * failure the cached promise is dropped, so a later caller - a retry button, a
 * second map, the `online` handler - starts a genuine new attempt.
 */
export function loadMapboxGl(): Promise<typeof mapboxgl> {
  if (glPromise) return glPromise;

  installOnlineRecovery();

  glPromise = withRetry(
    () =>
      withTimeout(
        Promise.all([loadScript(GL_JS), loadStylesheet(GL_CSS)]).then(() => {
          const gl = (window as any).mapboxgl as typeof mapboxgl | undefined;
          if (!gl) {
            forgetAsset(GL_JS);
            throw new MapboxContractError(
              '[mapbox] mapbox-gl.js loaded but did not define window.mapboxgl'
            );
          }
          return gl;
        }),
        'mapbox-gl.js',
        [GL_JS, GL_CSS]
      ),
    'mapbox-gl.js',
    'mapboxgl'
  )
    .then((gl) => {
      (gl as any).accessToken = environment.mapbox.accessToken;
      return gl;
    })
    .catch((error) => {
      console.error(
        '[mapbox] GL JS failed to load after every attempt, maps cannot render',
        error
      );
      hasFailed = true;
      glPromise = null;
      throw error;
    });

  return glPromise;
}

/**
 * Resolves with the global `MapboxGeocoder`. Depends on GL JS: the plugin is
 * handed `mapboxgl` for its marker, and its stylesheet assumes mapbox-gl.css.
 */
export function loadMapboxGeocoder(): Promise<typeof MapboxGeocoder> {
  if (geocoderPromise) return geocoderPromise;

  geocoderPromise = loadMapboxGl()
    .then(() =>
      withRetry(
        () =>
          withTimeout(
            Promise.all([
              loadScript(GEOCODER_JS),
              loadStylesheet(GEOCODER_CSS),
            ]).then(() => {
              const geocoder = (window as any).MapboxGeocoder as
                | typeof MapboxGeocoder
                | undefined;
              if (!geocoder) {
                forgetAsset(GEOCODER_JS);
                throw new MapboxContractError(
                  '[mapbox] mapbox-gl-geocoder loaded but did not define window.MapboxGeocoder'
                );
              }
              return geocoder;
            }),
            'mapbox-gl-geocoder.js',
            [GEOCODER_JS, GEOCODER_CSS]
          ),
        'mapbox-gl-geocoder.js',
        'MapboxGeocoder'
      )
    )
    .catch((error) => {
      console.error(
        '[mapbox] geocoder plugin failed to load after every attempt, address search is unavailable',
        error
      );
      hasFailed = true;
      geocoderPromise = null;
      throw error;
    });

  return geocoderPromise;
}

/**
 * True once the global exists - note that is a moment earlier than
 * `loadMapboxGl()` resolving, which also sets `accessToken`.
 *
 * For the handful of synchronous accessors that are only ever reached from a
 * map that has already been built, so they can say so loudly instead of
 * throwing a bare TypeError.
 */
export function isMapboxGlLoaded(): boolean {
  return typeof (window as any).mapboxgl !== 'undefined';
}

/** As `isMapboxGlLoaded`, for the geocoder plugin's own global. */
export function isMapboxGeocoderLoaded(): boolean {
  return typeof (window as any).MapboxGeocoder !== 'undefined';
}

/**
 * Fire-and-forget warm-up, called from `loadComponent` in the routing table so
 * the CDN fetch runs concurrently with the route chunk download rather than
 * after it. Rejections are already logged inside the loaders; swallow them
 * here so an unhandled rejection never reaches the console twice.
 */
export function preloadMapbox(options?: { geocoder?: boolean }): void {
  const work = options?.geocoder ? loadMapboxGeocoder() : loadMapboxGl();
  work.catch(() => {});
}

/**
 * WebGL check, kept next to the loader so every map entry point asks the same
 * question. `mapboxgl.supported()` covers "no WebGL at all" and "WebGL that is
 * too slow to be usable", both of which otherwise surface as an empty grey box.
 *
 * Unlike a load failure this is a property of the browser, so it is never
 * worth retrying - map entry points offer no retry when this is what failed.
 */
export function isMapboxSupported(gl: typeof mapboxgl): boolean {
  const supported = (gl as any).supported?.();
  if (supported === false) {
    console.error(
      '[mapbox] WebGL is unavailable or unusable in this browser, the map cannot render'
    );
    return false;
  }
  return true;
}
